import type { FoGesrPdfDocument } from '../infrastructure/foGesrPdfDocument.ts';
import { drawFoGesrHeaderTable, pintarPiePaginas } from '../infrastructure/foGesrPdfLayout.ts';
import {
  createFoGesrLandscapeA4Base,
  makeFoGesrCellLines,
  scaleTableColumnMm,
  writeFoGesrAvisoPrivacidadBlock,
  writeFoGesrMapUbicacionBlock,
  writeFoGesrMetaLabelValuePairs,
} from '../infrastructure/foGesrReportPdfBuildCore.ts';
import {
  acudienteImagenesOrdenPdf,
  actaCompromisoEntregada,
  celdaDocumentoYTipo,
  celdaNombrePersonaAutorizada,
  COL_WIDTHS_MM,
  comunaOCorregimientoPdf,
  docElectronicoId,
  formatFechaPdf,
  formatHora12,
  numeroEntregaPdf,
  siNo,
  splitFechaHora,
  TABLA_ENCABEZADOS,
  textoMayusEs,
  tipoBonoCeldaPdf,
  ubicacionPdf,
} from './puntoEntregaReportFormatting.ts';
import type { BuildPuntoEntregaReportPdfOpts, PuntoEntregaReportPdfDetail } from './puntoEntregaReportPdfData.ts';
import { drawParticipantImageRow, drawTextRow, type TableDrawContext } from './puntoEntregaReportTableDrawing.ts';

export { triggerPdfDownload } from '../infrastructure/foGesrPdfLayout.ts';
export type { BuildPuntoEntregaReportPdfOpts, PuntoEntregaReportPdfDetail } from './puntoEntregaReportPdfData.ts';
/** Write participantes table block. */


async function writeParticipantesTableBlock(
  tableCtx: TableDrawContext,
  detail: PuntoEntregaReportPdfDetail,
  opts: BuildPuntoEntregaReportPdfOpts,
  comunaTipo: string,
): Promise<void> {
  drawTextRow(tableCtx, TABLA_ENCABEZADOS, true);
  const rows = detail.participantes;
  if (rows.length === 0) {
    drawTextRow(
      tableCtx,
      ['—', tipoBonoCeldaPdf(comunaTipo), '—', '—', '—', '—', '—', '—', '—', '—', '—', '—'],
      false,
    );
    return;
  }
  for (const p of rows) {
    const nombre = opts.resolveParticipanteNombre(p.participanteId);
    const { documento, tipoDocumento } = opts.resolveDocumentoYTipoDocumento(p.participanteId);
    await drawParticipantImageRow(
      tableCtx,
      [
        (p.bonoId ? String(p.bonoId) : '') || '—',
        tipoBonoCeldaPdf(comunaTipo),
        celdaDocumentoYTipo(documento, tipoDocumento),
        textoMayusEs(nombre),
        celdaNombrePersonaAutorizada(p),
        '—',
        siNo(p.politicaTratamientos),
        '',
        '',
        siNo(p.asistencia),
        actaCompromisoEntregada(p.acta) ? 'SI' : 'NO',
        siNo(p.entregaBolsa),
      ],
      p.registroFotografico ?? '',
      p.firmaBase64 ?? '',
      acudienteImagenesOrdenPdf(p),
    );
  }
}
/** Write totales block. */


function writeTotalesBlock(
  doc: FoGesrPdfDocument,
  y: { current: number },
  margin: number,
  pageH: number,
  setBold: (size: number) => void,
  onNewPage: () => void,
  rows: PuntoEntregaReportPdfDetail['participantes'],
): void {
  y.current += 8;
  const bonosEntregados = rows.filter((p) => p.bonoId > 0).length;
  const capacitadas = rows.filter((p) => p.asistencia).length;
  const bolsas = rows.filter((p) => p.entregaBolsa).length;
  const actas = rows.filter((p) => actaCompromisoEntregada(p.acta)).length;

  setBold(9);
  const totales = [
    `NÚMERO DE BONOS ENTREGADOS: ${bonosEntregados}`,
    `NÚMERO DE PERSONAS CAPACITADAS: ${capacitadas}`,
    `NÚMERO DE ENTREGA DE BOLSAS REUTILIZABLES: ${bolsas}`,
    `NÚMERO DE ACTAS DE COMPROMISO: ${actas}`,
  ];
  for (const t of totales) {
    if (y.current + 6 > pageH - margin - 8) onNewPage();
    doc.text(t, margin, y.current);
    y.current += 5;
  }
  y.current += 4;
}

/**
 * Delivery report PDF in official **FO-GESR-2160** layout (same header, meta fields,
 * privacy notice, participant table, totals, LOCATION block (matching the printed
 * Colombian form label), electronic-document line, and `-- N of M --` footer).
 */
export async function buildPuntoEntregaReportPdf(
  detail: PuntoEntregaReportPdfDetail,
  opts: BuildPuntoEntregaReportPdfOpts,
): Promise<Blob> {
  const { doc, pageW, pageH, margin, usableW, y, setNormal, setBold, newPage: newPageContinuacionTabla } =
    await createFoGesrLandscapeA4Base();

  const colW = scaleTableColumnMm(COL_WIDTHS_MM, usableW);
  const lh = 2.65;
  const pad = 0.7;
  const cellLines = makeFoGesrCellLines(doc, colW, pad);

  const tableCtx: TableDrawContext = {
    doc,
    y,
    margin,
    pageH,
    w: colW,
    lh,
    pad,
    newPage: newPageContinuacionTabla,
    setNormal,
    setBold,
    cellLines,
  };

  const { fecha, hora } = splitFechaHora(detail.fechaHora);
  const responsableTxt = textoMayusEs((opts.resolveResponsableNombre(detail.responsableId) ?? '').trim() || '—');
  const comunaEtiqueta = comunaOCorregimientoPdf(opts.comunaNombre);
  const nEntrega = numeroEntregaPdf(detail, opts.entregaNombre);
  const lugarPdf = textoMayusEs((opts.lugarNombre || '—').trim());

  y.current = await drawFoGesrHeaderTable(doc, y.current, margin, usableW);
  y.current += 5;

  const metaPairs: [string, string][] = [
    ['LUGAR DE ENCUENTRO EDUCATIVO Y ENTREGA DE BONO:', lugarPdf],
    ['COMUNA O CORREGIMIENTO:', comunaEtiqueta],
    ['FECHA:', formatFechaPdf(fecha)],
    ['HORA DE ENCUENTRO:', formatHora12(hora)],
    ['RESPONSABLE:', responsableTxt],
    ['NÚMERO DE ENTREGA:', nEntrega],
  ];
  writeFoGesrMetaLabelValuePairs(
    { doc, y, margin, pageH, usableW, setNormal, setBold, onNewPage: newPageContinuacionTabla },
    metaPairs,
    'normal',
  );

  writeFoGesrAvisoPrivacidadBlock(
    doc,
    y,
    margin,
    pageH,
    usableW,
    setNormal,
    newPageContinuacionTabla,
  );

  await writeParticipantesTableBlock(tableCtx, detail, opts, comunaEtiqueta);

  writeTotalesBlock(doc, y, margin, pageH, setBold, newPageContinuacionTabla, detail.participantes);

  await writeFoGesrMapUbicacionBlock(
    { doc, y, margin, pageH, usableW, setBold, setNormal, onNewPage: newPageContinuacionTabla },
    {
      lat: detail.latitud ?? '',
      lon: detail.longitud ?? '',
      ubicacionDetalle: ubicacionPdf(detail),
      mapTitle: 'UBICACIÓN',
    },
  );

  setBold(8);
  doc.text('Documento Electrónico', margin, y.current);
  y.current += 4;
  setNormal(8);
  doc.text(`ID: ${docElectronicoId(detail)}`, margin, y.current);

  pintarPiePaginas(doc, pageW, pageH);

  return await doc.output('blob');
}
